import { InvalidCatalogDefinitionError } from "../errors.model";
import { asMemberId, type MemberId } from "../member-identity";
import type {
  QuestionnaireItem,
  QuestionnaireScore,
  QuestionnaireScoreBand,
  QuestionnaireSection,
} from "../questionnaire-definition.model";
import type { QuestionnaireScoreCalculation } from "../questionnaire-score-calculation";
import type { UnitValue } from "../value.model";

export const TOTAL_SCORE_ID = "total" as MemberId;

/** Score references may name the system-derived Total or a minted subscore. */
export function asQuestionnaireScoreId(value: string): MemberId {
  return value === TOTAL_SCORE_ID ? TOTAL_SCORE_ID : asMemberId(value);
}

export function hasOptionPoints(item: {
  kind: string;
  value?: { kind: string; options?: readonly { points?: number }[] };
}): boolean {
  return (
    item.kind === "Question" &&
    item.value?.kind === "Choice" &&
    !!item.value.options?.some((option) => option.points !== undefined)
  );
}

export function hasNumericValue(item: { kind: string; value?: { kind: string } }): boolean {
  return item.kind === "Question" && (item.value?.kind === "Integer" || item.value?.kind === "Quantity");
}

/** Shared derivation for domain and read-side definitions; never persisted. */
export function implicitTotalScore<Id extends string>(
  sections: readonly {
    items: readonly { id?: Id; kind: string; value?: { kind: string; options?: readonly { points?: number }[] } }[];
  }[],
  numericValues: boolean,
  scores: readonly { calculation?: QuestionnaireScoreCalculation }[],
): { id: MemberId; label: string; itemIds: Id[] } | undefined {
  // Comparing tasks is not adding them: a dual-task measure's times have no meaningful sum.
  if (scores.some((score) => scoreCalculation(score.calculation).suppressesTotal)) return undefined;
  const items = sections.flatMap((section) => section.items);
  if (numericValues && items.some(hasOptionPoints) && items.some(hasNumericValue)) return undefined;
  const itemIds = sections.flatMap((section) =>
    section.items
      .filter((item) => hasOptionPoints(item) || (numericValues && hasNumericValue(item)))
      .flatMap((item) => (item.id ? [item.id] : [])),
  );
  return itemIds.length ? { id: TOTAL_SCORE_ID, label: "Total", itemIds } : undefined;
}

/** Shared by definition validation and the editor's item picker. */
export function scoreItemIneligibility(
  item: {
    kind: string;
    required?: boolean;
    conditions?: readonly unknown[];
    value?: { kind: string; cardinality?: string; allowFreeText?: boolean; options?: readonly { points?: number }[] };
  },
  section: { conditions?: readonly unknown[] },
  repeating = false,
  numericValues = false,
): string | undefined {
  if (item.kind !== "Question") return "Only questions can be scored";
  if (repeating) return "Inside a repeating section";
  if (section.conditions?.length || item.conditions?.length) return "Has a visibility condition";
  if (!item.required) return "Must be required";
  if (numericValues && hasNumericValue(item)) return undefined;
  if (item.value?.kind !== "Choice") return "Must be a choice question";
  if (item.value.cardinality !== "Single") return "Must allow a single selection";
  if (item.value.allowFreeText) return "Must not allow free text";
  if (item.value.options?.some((option) => option.points === undefined)) return "Every option needs points";
  return undefined;
}

/** An empty list is no bands at all, so a definition never carries both spellings. */
export function normalizedBands(
  bands: readonly QuestionnaireScoreBand[] | undefined,
): QuestionnaireScoreBand[] | undefined {
  return bands?.length
    ? bands.map((band) => ({ label: band.label, from: band.from, ...(band.to === undefined ? {} : { to: band.to }) }))
    : undefined;
}

/** The last start at or below the value applies only through its inclusive end. */
export function bandFor(
  bands: readonly QuestionnaireScoreBand[] | undefined,
  value: number,
): QuestionnaireScoreBand | undefined {
  const band = bands?.filter((band) => band.from <= value).at(-1);
  return band && (band.to === undefined || value <= band.to) ? band : undefined;
}

/** Lowest and highest attainable values. Callers validate the items first. */
export function scoreRange(
  score: Pick<QuestionnaireScore, "itemIds" | "calculation">,
  sections: readonly QuestionnaireSection[],
): { minimum: number; maximum: number } {
  const { itemIds } = score;
  const questions = new Map<MemberId, QuestionnaireItem>();
  for (const section of sections)
    for (const item of section.items) {
      if (item.id) questions.set(item.id, item);
      if (item.kind === "RepeatingGroup") for (const child of item.items) if (child.id) questions.set(child.id, child);
    }
  return scoreItemsRange(
    itemIds.flatMap((id) => {
      const item = questions.get(id);
      return item ? [item] : [];
    }),
    score.calculation,
  );
}

/** Attainable range shared by domain validation, authoring and charting previews. */
type ScoreItem = {
  id?: string;
  kind: string;
  value?: {
    kind: string;
    min?: number;
    max?: number;
    unit?: UnitValue;
    options?: readonly { id?: string; points?: number }[];
  };
};
type ScoringScore<ItemId extends string> = {
  label: string;
  itemIds: readonly ItemId[];
  bands?: readonly QuestionnaireScoreBand[];
};
type ScoreAnswer<ItemId extends string> = {
  questionId: ItemId;
  value: { kind: string; optionId?: ItemId; value?: unknown };
};

export function scoreItemsRange(items: readonly ScoreItem[], calculation?: QuestionnaireScoreCalculation) {
  return scoreCalculation(calculation).range(items);
}

function sumRange(items: readonly ScoreItem[]): { minimum: number; maximum: number } {
  return items.reduce(
    (range, item) => {
      if (item.kind !== "Question") return range;
      if (item.value?.kind === "Integer" || item.value?.kind === "Quantity")
        return {
          minimum: range.minimum + (item.value.min ?? -Infinity),
          maximum: range.maximum + (item.value.max ?? Infinity),
        };
      const points =
        item.value?.kind === "Choice" ? (item.value.options?.map((option) => option.points ?? 0) ?? [0]) : [0];
      return { minimum: range.minimum + Math.min(...points), maximum: range.maximum + Math.max(...points) };
    },
    { minimum: 0, maximum: 0 },
  );
}

function validateBands(
  label: string,
  bands: readonly QuestionnaireScoreBand[],
  range: { minimum: number; maximum: number },
): void {
  const refuse = (reason: string) => {
    throw new InvalidCatalogDefinitionError(`Score '${label}' bands: ${reason}`);
  };
  const seen = new Set<string>();
  for (const [at, band] of bands.entries()) {
    const name = band.label.trim();
    if (!name) refuse("every band needs a label");
    if (seen.has(name.toLowerCase())) refuse(`'${name}' appears twice`);
    seen.add(name.toLowerCase());
    if (!Number.isInteger(band.from)) refuse(`'${name}' must start at a whole number`);
    if (band.to !== undefined) {
      if (!Number.isInteger(band.to)) refuse(`'${name}' must end at a whole number`);
      if (band.to < band.from) refuse(`'${name}' must end at or above its start`);
      if (band.to < range.minimum || band.to > range.maximum) refuse(`'${name}' end must be within the score range`);
    }
    const prior = bands[at - 1];
    if (prior && band.from <= prior.from) refuse("starts must be strictly ascending");
    if (prior?.to !== undefined && prior.to >= band.from) refuse("bands must not overlap");
  }

  if (bands.at(-1)!.from > range.maximum) refuse(`no band may start above the maximum, ${range.maximum}`);
}

function validatePercentChange(
  score: QuestionnaireScore,
  items: readonly QuestionnaireItem[],
  numericValues: boolean,
): void {
  const refuse: (reason: string) => never = (reason) => {
    throw new InvalidCatalogDefinitionError(`Score '${score.label}': ${reason}`);
  };
  if (!numericValues) refuse("percent change is only for performance measures");
  if (items.length !== 2) refuse("percent change compares exactly two tasks, baseline then comparison");
  const [baseline, comparison] = items.map((item) =>
    item.kind === "Question" && item.value.kind === "Quantity" ? item.value : undefined,
  );
  if (!baseline || !comparison) refuse("percent change needs two quantity tasks");
  if (baseline.unit?.code !== comparison.unit?.code) refuse("percent change needs both tasks in the same unit");
  if (!(baseline.min !== undefined && baseline.min > 0)) refuse("the baseline task needs a minimum above zero");
  if (score.bands?.length) refuse("percent change scores carry no bands");
}

export function validateQuestionnaireScores(
  scores: readonly QuestionnaireScore[],
  sections: readonly QuestionnaireSection[],
  totalBands?: readonly QuestionnaireScoreBand[],
  numericValues = false,
): void {
  const items = new Map<MemberId, { item: QuestionnaireItem; reason?: string }>();
  for (const section of sections) {
    for (const item of section.items) {
      if (item.id) items.set(item.id, { item, reason: scoreItemIneligibility(item, section, false, numericValues) });
      if (item.kind === "RepeatingGroup")
        for (const child of item.items) {
          if (child.id)
            items.set(child.id, { item: child, reason: scoreItemIneligibility(child, section, true, numericValues) });
        }
    }
  }
  for (const [id, entry] of items) {
    if (hasOptionPoints(entry.item) && entry.reason)
      throw new InvalidCatalogDefinitionError(
        `Total, item '${entry.item.kind === "Question" ? entry.item.label : id}' (${id}): ${entry.reason}`,
      );
  }
  for (const score of scores) {
    if (score.id === TOTAL_SCORE_ID || score.label.trim().toLowerCase() === "total")
      throw new InvalidCatalogDefinitionError("Total is reserved and must not be declared");
    if (!score.label.trim()) throw new InvalidCatalogDefinitionError("A score must have a non-blank label");
    if (!score.itemIds.length)
      throw new InvalidCatalogDefinitionError(`Score '${score.label}' needs at least one item`);
    const seen = new Set<MemberId>();
    for (const id of score.itemIds) {
      const entry = items.get(id);
      const label = entry?.item.kind === "Question" ? entry.item.label : id;
      const reason = seen.has(id) ? "appears twice" : !entry ? "does not exist" : entry.reason;
      if (reason) throw new InvalidCatalogDefinitionError(`Score '${score.label}', item '${label}' (${id}): ${reason}`);
      seen.add(id);
    }
    const selected = [...seen].map((id) => items.get(id)!.item);
    if (numericValues && selected.some(hasOptionPoints) && selected.some(hasNumericValue))
      throw new InvalidCatalogDefinitionError(`Score '${score.label}' cannot mix choice points and numeric values`);
    scoreCalculation(score.calculation).validate(score, selected, numericValues);
    if (score.bands?.length) validateBands(score.label, score.bands, scoreRange(score, sections));
  }
  if (totalBands?.length) {
    const total = implicitTotalScore(sections, numericValues, scores);
    if (!total)
      throw new InvalidCatalogDefinitionError(
        "Total bands require an implicit Total; mixed choice and numeric measures have no Total",
      );
    validateBands(total.label, totalBands, scoreRange(total, sections));
  }
}

/** The same published-key calculation serves completed records and complete previews. Missing grades refuse a total. */
export function computeQuestionSetScores<Id extends string, ItemId extends string>(
  sections: readonly {
    items: readonly {
      id?: ItemId;
      kind: string;
      value?: { kind: string; unit?: UnitValue; options?: readonly { id?: ItemId; points?: number }[] };
    }[];
  }[],
  scores: readonly {
    id?: Id;
    label: string;
    itemIds: readonly ItemId[];
    bands?: readonly QuestionnaireScoreBand[];
    calculation?: QuestionnaireScoreCalculation;
  }[],
  answers: readonly { questionId: ItemId; value: { kind: string; optionId?: ItemId; value?: unknown } }[],
): { scoreId: Id; value: number; band?: string; unit?: UnitValue }[] {
  return scores.map((score) => {
    const { value, unit } = scoreCalculation(score.calculation).compute(
      score,
      sections.flatMap((section) => section.items),
      answers,
    );
    const band = bandFor(score.bands, value);
    return { scoreId: score.id!, value, ...(band ? { band: band.label } : {}), ...(unit ? { unit: { ...unit } } : {}) };
  });
}

interface CalculationDescriptor {
  suppressesTotal: boolean;
  allowsBands: boolean;
  maximumItems?: number;
  unit?: UnitValue;
  range: (items: readonly ScoreItem[]) => { minimum: number; maximum: number };
  compute: <ItemId extends string>(
    score: ScoringScore<ItemId>,
    items: readonly ScoreItem[],
    answers: readonly ScoreAnswer<ItemId>[],
  ) => { value: number; unit?: UnitValue };
  validate: (score: QuestionnaireScore, items: readonly QuestionnaireItem[], numericValues: boolean) => void;
}

const sumCalculation: CalculationDescriptor = {
  suppressesTotal: false,
  allowsBands: true,
  range: sumRange,
  validate: () => {},
  compute: (score, items, answers) => {
    const value = score.itemIds.reduce((total, id) => {
      const question = items.find((item) => item.id === id);
      const answer = answers.find((candidate) => candidate.questionId === id);
      if (
        question &&
        answer &&
        hasNumericValue(question) &&
        answer.value.kind === question.value?.kind &&
        typeof answer.value.value === "number" &&
        Number.isFinite(answer.value.value)
      )
        return total + answer.value.value;
      const selectedId = answer?.value.kind === "Choice" ? answer.value.optionId : undefined;
      const option =
        question?.kind === "Question" && question.value?.kind === "Choice" && answer?.value.kind === "Choice"
          ? question.value.options?.find((option) => option.id === selectedId)
          : undefined;
      if (option?.points === undefined)
        throw new InvalidCatalogDefinitionError(`Score '${score.label}' requires an option answer for item '${id}'`);
      return total + option.points;
    }, 0);
    const task = score.itemIds.length === 1 ? items.find((item) => item.id === score.itemIds[0]) : undefined;
    const unit = task?.kind === "Question" && task.value?.kind === "Quantity" ? task.value.unit : undefined;
    return { value, unit };
  },
};

const calculations: Record<QuestionnaireScoreCalculation, CalculationDescriptor> = {
  "percent-change": {
    suppressesTotal: true,
    allowsBands: false,
    maximumItems: 2,
    unit: { code: "%", display: "%" },
    range: () => ({ minimum: -100, maximum: Infinity }),
    validate: validatePercentChange,
    compute: (score, _items, answers) => {
      const [baseline, comparison] = score.itemIds.map((id) => {
        const value = answers.find((candidate) => candidate.questionId === id)?.value.value;
        if (typeof value !== "number" || !Number.isFinite(value))
          throw new InvalidCatalogDefinitionError(`Score '${score.label}' requires a numeric answer for item '${id}'`);
        return value;
      }) as [number, number];
      return { value: Math.round(((comparison - baseline) / baseline) * 1000) / 10, unit: { code: "%", display: "%" } };
    },
  },
};

export function scoreCalculation(calculation?: QuestionnaireScoreCalculation): CalculationDescriptor {
  return calculation ? calculations[calculation] : sumCalculation;
}
